// ============================================
// ハンバーガーメニュー & ドロップダウンナビゲーション
// ============================================

/* CSS変数からブレークポイントを取得 */
const BREAKPOINT_LG = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint'), 10);

/* フォーカストラップ用: tabindex="-1" を除外したフォーカス可能要素のセレクタ */
const FOCUSABLE_SELECTOR = [
	'a[href]:not([tabindex="-1"])',
	'button:not(:disabled):not([tabindex="-1"])',
	'input:not(:disabled):not([tabindex="-1"])',
	'textarea:not(:disabled):not([tabindex="-1"])',
	'select:not(:disabled):not([tabindex="-1"])',
	'[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * ハンバーガーメニューを初期化
 */
export function initHamburger() {
	const button = document.querySelector('.header-button');
	const nav = document.getElementById('global-navigation');
	if (!button || !nav) return;

	const mql = window.matchMedia(`(min-width: ${BREAKPOINT_LG}px)`);
	let focusTrapHandler = null;

	/* 変更: 初期状態の設定（SP/PC分岐） */
	if (mql.matches) {
		/* PC: ナビは常に表示、aria-hidden="false" */
		nav.setAttribute('aria-hidden', 'false');
	} else {
		/* SP: ナビ内の全フォーカス可能要素を無効化 */
		setNavFocusable(nav, false);
	}

	/* ハンバーガーボタン クリック */
	button.addEventListener('click', () => {
		const isOpen = button.getAttribute('aria-expanded') === 'true';
		toggleMenu(!isOpen);
	});

	/* SP: ナビ内リンク（ページ内遷移）クリックでメニューを閉じる */
	const gnavJobsLink = nav.querySelector('.gnav-jobs__link');
	if (gnavJobsLink) {
		gnavJobsLink.addEventListener('click', () => {
			if (mql.matches) return;
			toggleMenu(false);
		});
	}

	/* Escapeキー でメニューを閉じる */
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && document.documentElement.classList.contains('is-gnav-open')) {
			toggleMenu(false);
			button.focus();
		}
	});

	/* 変更: ブレークポイント変更時の処理を拡張 */
	mql.addEventListener('change', (e) => {
		if (e.matches) {
			/* SP → PC: メニュー閉鎖、ナビ常時表示 */
			toggleMenu(false);
			nav.setAttribute('aria-hidden', 'false');
			setNavFocusable(nav, true);
		} else {
			/* PC → SP: ナビ非表示、フォーカス無効化 */
			nav.setAttribute('aria-hidden', 'true');
			setNavFocusable(nav, false);
			resetAllDropdowns();
		}
	});

	/* ドロップダウン初期化 */
	initDropdowns(mql);

	/**
	 * メニュー開閉
	 * @param {boolean} open - true: 開く / false: 閉じる
	 */
	function toggleMenu(open) {
		document.documentElement.classList.toggle('is-gnav-open', open);
		button.setAttribute('aria-expanded', String(open));
		button.setAttribute('aria-label', open ? 'メニューを閉じる' : 'メニューを開く');

		/* 変更: SPのみaria-hiddenとtabindex制御（PCでは常にfalse） */
		if (!mql.matches) {
			nav.setAttribute('aria-hidden', String(!open));
			setNavFocusable(nav, open);
		}

		/* フォーカストラップ制御 */
		if (open && !mql.matches) {
			enableFocusTrap(button, nav);
		} else if (!open) {
			disableFocusTrap();
		}

		/* メニューを閉じる時はドロップダウンもリセット */
		if (!open) {
			resetAllDropdowns();
		}
	}

	/**
	 * フォーカストラップを有効化（SPメニュー開放時）
	 * ハンバーガーボタン + ナビ内のフォーカス可能要素でTabキーをループさせる
	 */
	function enableFocusTrap(trapButton, trapNav) {
		focusTrapHandler = (e) => {
			if (e.key !== 'Tab') return;

			/* ドロップダウン開閉で変化するため、毎回動的に取得 */
			const focusableInNav = [...trapNav.querySelectorAll(FOCUSABLE_SELECTOR)];
			const focusableElements = [trapButton, ...focusableInNav];

			if (focusableElements.length === 0) return;

			const firstEl = focusableElements[0];
			const lastEl = focusableElements[focusableElements.length - 1];

			if (e.shiftKey && document.activeElement === firstEl) {
				/* Shift+Tab: 最初の要素 → 最後の要素へループ */
				e.preventDefault();
				lastEl.focus();
			} else if (!e.shiftKey && document.activeElement === lastEl) {
				/* Tab: 最後の要素 → 最初の要素へループ */
				e.preventDefault();
				firstEl.focus();
			}
		};

		document.addEventListener('keydown', focusTrapHandler);
	}

	/**
	 * フォーカストラップを無効化（SPメニュー閉鎖時）
	 */
	function disableFocusTrap() {
		if (focusTrapHandler) {
			document.removeEventListener('keydown', focusTrapHandler);
			focusTrapHandler = null;
		}
	}
}

/**
 * ドロップダウンナビゲーションを初期化
 * @param {MediaQueryList} mql - PCブレークポイントのMediaQueryList
 */
function initDropdowns(mql) {
	const triggers = document.querySelectorAll('[data-dropdown]');

	triggers.forEach((trigger) => {
		const targetId = trigger.getAttribute('data-dropdown');
		const dropdown = document.getElementById(targetId);
		if (!dropdown) return;

		const parentItem = trigger.closest('.gnav-list__item');

		/* 変更: PC用 遅延クローズタイマー */
		let pcCloseTimer = null;

		/* 初期状態: ドロップダウン内のフォーカスを無効化 */
		setDropdownFocusable(dropdown, false);

		/* ----- SP: クリックで開閉 ----- */
		trigger.addEventListener('click', () => {
			if (mql.matches) return;

			const isOpen = trigger.getAttribute('aria-expanded') === 'true';

			if (isOpen) {
				closeDropdown(trigger, dropdown);
			} else {
				openDropdown(trigger, dropdown);
			}
		});

		/* ----- 変更: PC ホバー（トリガー + ドロップダウン両方を対象） ----- */
		/* トリガーにマウス入 → 開く */
		trigger.addEventListener('mouseenter', () => {
			if (!mql.matches) return;
			showDropdownPC();
		});

		/* トリガーからマウス出 → 遅延クローズ予約 */
		trigger.addEventListener('mouseleave', () => {
			if (!mql.matches) return;
			scheduleClosePC();
		});

		/* ドロップダウンにマウス入 → クローズキャンセル */
		dropdown.addEventListener('mouseenter', () => {
			if (!mql.matches) return;
			clearTimeout(pcCloseTimer);
		});

		/* ドロップダウンからマウス出 → 遅延クローズ予約 */
		dropdown.addEventListener('mouseleave', () => {
			if (!mql.matches) return;
			scheduleClosePC();
		});

		/* ----- 変更: PC フォーカスで開閉 ----- */
		/* トリガーにTabフォーカス → 開く */
		trigger.addEventListener('focus', () => {
			if (!mql.matches) return;
			showDropdownPC();
		});

		/* 親アイテムからフォーカスが完全に離れたら閉じる */
		if (parentItem) {
			parentItem.addEventListener('focusout', (e) => {
				if (!mql.matches) return;
				/* フォーカスの移動先が親アイテム外なら閉じる */
				if (!parentItem.contains(e.relatedTarget)) {
					hideDropdownPC();
				}
			});
		}

		/**
		 * PC: ドロップダウンを表示
		 */
		function showDropdownPC() {
			clearTimeout(pcCloseTimer);
			dropdown.style.opacity = '1';
			dropdown.style.pointerEvents = 'auto';
			trigger.setAttribute('aria-expanded', 'true');
			trigger.classList.add('is-hover');
			dropdown.setAttribute('aria-hidden', 'false');
			setDropdownFocusable(dropdown, true);
		}

		/**
		 * PC: 遅延クローズ予約（トリガー↔ドロップダウン間のマウス移動を許容）
		 */
		function scheduleClosePC() {
			clearTimeout(pcCloseTimer);
			pcCloseTimer = setTimeout(() => {
				hideDropdownPC();
			}, 80);
		}

		/**
		 * PC: ドロップダウンを非表示
		 */
		function hideDropdownPC() {
			clearTimeout(pcCloseTimer);
			dropdown.style.opacity = '';
			dropdown.style.pointerEvents = '';
			trigger.setAttribute('aria-expanded', 'false');
			trigger.classList.remove('is-hover');
			dropdown.setAttribute('aria-hidden', 'true');
			setDropdownFocusable(dropdown, false);
		}
	});
}

/**
 * ナビ内のフォーカス可否を切替（ドロップダウン内は除外・別途管理）
 * @param {HTMLElement} nav - ナビゲーション要素
 * @param {boolean} enabled - true: フォーカス可 / false: フォーカス不可
 */
function setNavFocusable(nav, enabled) {
	const allFocusables = nav.querySelectorAll('a[href], button:not(:disabled)');
	const dropdowns = nav.querySelectorAll('.gnav-dropdown');

	/* ドロップダウン内の要素をSetに格納（除外用） */
	const dropdownElements = new Set();
	dropdowns.forEach((dd) => {
		dd.querySelectorAll('a[href], button:not(:disabled)').forEach((el) => {
			dropdownElements.add(el);
		});
	});

	allFocusables.forEach((el) => {
		if (dropdownElements.has(el)) return;

		if (enabled) {
			el.removeAttribute('tabindex');
		} else {
			el.setAttribute('tabindex', '-1');
		}
	});
}

/**
 * ドロップダウン内のフォーカス可否を切替
 * @param {HTMLElement} dropdown - ドロップダウン要素
 * @param {boolean} enabled - true: フォーカス可 / false: フォーカス不可
 */
function setDropdownFocusable(dropdown, enabled) {
	const focusables = dropdown.querySelectorAll('a[href], button:not(:disabled)');

	focusables.forEach((el) => {
		if (enabled) {
			el.removeAttribute('tabindex');
		} else {
			el.setAttribute('tabindex', '-1');
		}
	});
}

/**
 * ドロップダウンを開く（SP用・高さアニメーション）
 */
function openDropdown(trigger, dropdown) {
	trigger.setAttribute('aria-expanded', 'true');
	dropdown.setAttribute('aria-hidden', 'false');
	dropdown.style.height = dropdown.scrollHeight + 'px';
	setDropdownFocusable(dropdown, true);

	/* 変更: アニメーション完了後にheight:autoに変更（レスポンシブ対応） */
	const onTransitionEnd = (e) => {
		if (e.propertyName !== 'height') return;
		dropdown.style.height = 'auto';
		dropdown.removeEventListener('transitionend', onTransitionEnd);
	};
	dropdown.addEventListener('transitionend', onTransitionEnd);
}

/**
 * ドロップダウンを閉じる（SP用・高さアニメーション）
 */
function closeDropdown(trigger, dropdown) {
	trigger.setAttribute('aria-expanded', 'false');
	dropdown.setAttribute('aria-hidden', 'true');

	/* 変更: height:autoからアニメーションするため、現在の高さを明示的に設定 */
	dropdown.style.height = dropdown.scrollHeight + 'px';
	dropdown.offsetHeight; /* Force reflow */
	dropdown.style.height = '0';

	setDropdownFocusable(dropdown, false);
}

/**
 * 全ドロップダウンをリセット（メニュー閉鎖時・ブレークポイント変更時）
 */
function resetAllDropdowns() {
	const triggers = document.querySelectorAll('[data-dropdown]');

	triggers.forEach((trigger) => {
		const targetId = trigger.getAttribute('data-dropdown');
		const dropdown = document.getElementById(targetId);
		if (!dropdown) return;

		trigger.setAttribute('aria-expanded', 'false');
		dropdown.setAttribute('aria-hidden', 'true');

		/* インラインスタイルをリセット（CSSのデフォルト値に戻す） */
		dropdown.style.height = '';
		dropdown.style.opacity = '';
		dropdown.style.pointerEvents = '';

		setDropdownFocusable(dropdown, false);
	});
}
